import { Box, Button, CircularProgress, FormControl, IconButton, Menu, MenuItem, OutlinedInput, Pagination, Select, Typography } from "@mui/material";
import { DataGrid, GridToolbarColumnsButton } from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPermissionData, getUserData } from "../../Actions/common";
import { getTenantAction } from "../../Actions/TenantActions";
import { exportTransactionHistoryAction, listTransactionDataAction } from "../../Actions/TransactionActions";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { CustomNoRowsOverlay } from "../../Assets/customTheme/CustomNoRowsOverlay";
import { default as CoustomTooltip, default as CustomTooltip } from "../../Assets/customTheme/customtooltip";
import { TableColumns, TableFilte, TableReset } from "../../Assets/svg/svgicon";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS} from "../../config";
import { formatCurrency } from "../../utils/CommonFunctions";
import { formatDateTime } from "../Common/DateFormat";
import ExportDialog from "../Common/ExportModal";
import CustomSnackbar from "../Common/snackBar";
import TransactionCards from "./TransactionCards";
import renderStatusBadge from "../../utils/badges";
import { MyDescriptionOutlinedIcon, MyExportIcon, MySearchIcon } from "../../Assets/svg/MyIcons";
import DateRangePicker from "../Common/DateRangePicker";
import TransactionInvoiceModal from "./TransactionInvoiceModal";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return { from: start, to: end };
};

const CustomToolbar = ({
  SelectDropdown_SX,
  searchValue,
  handleSearchApi,
  handleFilterData,
  handleResetFilters,
  isDashboardView,
  handleChangeDropdown,
  statusFilter,
  amountFilter,
  tenantFilter,
  merchantData,
  handleExportOption,
  handleExportClick,
  handleMenuClose,
  isMenuOpen,
  anchorEl,
  filterButtonDisabled,
  resetButtonDisabled,
  hasPermission,
  exportLoader,
  transactionData,
  selectedRange,
  onRangeChange,
}) => {
  const navigate = useNavigate();
  if (isDashboardView) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px 25px 16px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
          }}
        >
          Recent Transactions
        </Typography>

        <span
          className="btn btn-primary btn-sm d-flex align-items-center"
          onClick={() => navigate("/transactions")}
        >
          View All
        </span>
      </Box>
    );
  }

  return (
    <div
      className="custom-header"
      style={{
        overflowX: "auto",
        whiteSpace: "nowrap",
      }}
    >
      <div className="card border-rounded-0 fiter-card">
        <div className="card-body p-5 pb-0">
          <div className="row">
            {/* Search */}
            <div className="col-xl-3 col-xxl-2 col-lg-3 col-md-4 col-sm-6 mb-5">
              <div className="position-relative">
                <MySearchIcon className="fs-2 text-gray-500 position-absolute top-50 translate-middle-y ms-4" />
                <input
                  type="text"
                  className="search-input form-control ps-12"
                  placeholder="Search with Transaction ID"
                  name="search"
                  value={searchValue}
                  onChange={handleSearchApi}
                />
              </div>
            </div>

            {/* Date */}
            <div className="col-xl-3 col-xxl-2 col-lg-3 col-md-3 col-sm-6 mb-5">
              <DateRangePicker
                selectedRange={selectedRange}
                onRangeChange={onRangeChange}
                maxDate={new Date()}
                placeholder="Select date range"
              />
            </div>

            {/* Status */}
            <div className="col-xl-3 col-xxl-2 col-lg-3 col-md-3 col-sm-6 mb-5">
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={statusFilter}
                  onChange={(e) => handleChangeDropdown(e, "statusFilter")}
                  input={<OutlinedInput />}
                  MenuProps={MenuProps}
                  sx={SelectDropdown_SX}
                >
                  <MenuItem value="all">Select Status</MenuItem>
                  <MenuItem value="0">Pending</MenuItem>
                  <MenuItem value="1">Success</MenuItem>
                  <MenuItem value="2">Failed</MenuItem>
                  <MenuItem value="3">Refund</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Merchant */}
            <div className="col-xl-3 col-xxl-2 col-lg-3 col-md-3 col-sm-6 mb-5">
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={tenantFilter}
                  onChange={(e) => handleChangeDropdown(e, "tenantFilter")}
                  input={<OutlinedInput />}
                  MenuProps={MenuProps}
                  sx={SelectDropdown_SX}
                  disabled={merchantData?.length === 0}
                >
                  <MenuItem value="all">Select Tenant</MenuItem>
                  {merchantData?.map((merchant) => (
                    <MenuItem
                      key={merchant.id}
                      value={merchant.id}
                    >
                      {merchant.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Action Buttons */}
            <div className="col-auto ms-auto d-flex align-items-center gap-2 mb-5">
              <CustomTooltip
                title="Filter"
                placement="top"
                arrow
              >
                <span>
                <button
                  className="btn text-primary btn-light btn-icon"
                  onClick={handleFilterData}
                  disabled={filterButtonDisabled}
                >
                  <TableFilte />
                </button>
                </span>
              </CustomTooltip>

              <CustomTooltip
                title="Reset"
                placement="top"
                arrow
              >
                <span>
                <button
                  className="btn btn-light text-primary btn-icon"
                  onClick={handleResetFilters}
                  disabled={resetButtonDisabled}
                >
                  <TableReset />
                </button>
                </span>
              </CustomTooltip>

              <CoustomTooltip
                title="Column Filter"
                placement="top"
                arrow
              >
                <span style={{ display: "inline-block" }}>
                  <GridToolbarColumnsButton
                    slotProps={{
                      tooltip: { title: "" },
                      button: {
                        className: "btn btn-light text-primary btn-icon grid-toolbar d-flex justify-content-center align-items-center p-0",
                        style: {
                          width: "47px",
                          height: "44px",
                          minWidth: "unset",
                          borderRadius: "8px",
                        },
                        startIcon: <TableColumns />,
                      },
                    }}
                  />
                </span>
              </CoustomTooltip>

              {hasPermission?.canExport && (
                <>
                  <CoustomTooltip>
                    <span>
                    <Button
                      className="btn btn-primary"
                      aria-controls={isMenuOpen ? "export-menu" : undefined}
                      aria-haspopup="true"
                      disabled={exportLoader || transactionData.length === 0}
                      onClick={handleExportClick}
                    >
                      <MyExportIcon className="text-white h-2 w-2 me-1"/> Export
                      {exportLoader && (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                    </Button>
                    </span>
                  </CoustomTooltip>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleExportOption("Excel")}>Export as Excel</MenuItem>
                    <MenuItem onClick={() => handleExportOption("PDF")}>Export as PDF</MenuItem>
                  </Menu>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TransactionSection = ({ isDashboardView = false }) => {
  const [selectedColumns, setSelectedColumns] = useState([]);

  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(isDashboardView ? 5 : ITEMS_PER_PAGE_OPTIONS[0]);

  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [loading, setLoading] = useState(true);
  const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(true);
  const [transactionData, setTransactionData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [enableFilterButton, setEnableFilterButton] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [shouldFetchData, setShouldFetchData] = useState(1);

  const [statusFilter, setStatusFilter] = useState("all");
  const [amountFilter, setAmountFilter] = useState("all");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [websiteDisabled, setWebsiteDisabled] = useState(true);
  const [merchantData, setMerchantData] = useState([]);
  
  // Date range states
  const defaultRange = getDefaultDateRange();
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [startDate, setStartDate] = useState(defaultRange.from.toISOString());
  const [endDate, setEndDate] = useState(defaultRange.to.toISOString());
  const [exportType, setExportType] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState("thisPage");
  const [exportLoader, setExportLoader] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`) 
    ? getPermissionData(`${userData?.id}_permissionData`) 
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find((m) => m?.module && m?.module?.slug === "transaction");
  const [shouldRefreshCards, setShouldRefreshCards] = useState(0);
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSnackBar = (severity = null, message) => {
    if (severity)
      setSnackbar({
        open: true,
        message: message || "Something went wrong...!",
        severity,
      });
  };

  const getMerchantListData = async () => {
    await getTenantAction(undefined, undefined, undefined, undefined, "1")
      .then((res) => {
        const { data } = res;
        setMerchantData(data.data);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Something went wrong. Please try again later.",
          severity: "error",
        });
      });
  };

  const getTransactionData = useCallback(async () => {
    setLoading(true);
    await listTransactionDataAction(selectedRowsPerPage, currentPage, searchValue, startDate, endDate, statusFilter, amountFilter, tenantFilter)
      .then((res) => {
        const { data } = res;
        setTransactionData(data.data || data || []);
        setTotalRecords(data?.totalRecords || 0);
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          setSnackbar({
            open: true,
            message: error?.response?.data?.message,
            severity: "error",
          });
        } else {
          setSnackbar({
            open: true,
            message: error?.response?.data?.message,
            severity: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedRowsPerPage, currentPage, searchValue, startDate, endDate, statusFilter, amountFilter, tenantFilter]);

  useEffect(() => {
    getMerchantListData();
  }, []);

  useEffect(() => {
    if (shouldFetchData) {
      getTransactionData();
      setShouldFetchData(0);
    }
  }, [shouldFetchData, getTransactionData]);

  const handleSearchApi = (e) => {
    const inputValue = e.target.value;
    if (inputValue.length === 0) {
      setFilterButtonDisabled(true);
    } else {
      setFilterButtonDisabled(false);
    }
    setSearchValue(inputValue);
    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  const handleRangeChange = (range) => {
    setFilterButtonDisabled(false);
    setResetButtonDisabled(false);

    if (!range) {
      setSelectedRange({ from: undefined, to: undefined });
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setSelectedRange(range);

    if (range.from) {
      const year = range.from.getFullYear();
      const month = range.from.getMonth();
      const day = range.from.getDate();
      const startDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      setStartDate(startDateObj.toISOString());
    }

    if (range.to) {
      const year = range.to.getFullYear();
      const month = range.to.getMonth();
      const day = range.to.getDate();
      const endDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      setEndDate(endDateObj.toISOString());
    }
  };

  const handleResetFilters = () => {
    const defaultRange = getDefaultDateRange();

    setLoading(true);
    setSearchValue("");
    setCurrentPage(1);
    setShouldFetchData(1);
    setEnableFilterButton(false);
    setLoading(true);
    setTransactionData([]);
    setSelectedRange(defaultRange);
    setStartDate(defaultRange.from.toISOString());
    setEndDate(defaultRange.to.toISOString());
    setStatusFilter("all");
    setAmountFilter("all");
    setTenantFilter("all");
    setResetButtonDisabled(true);
    setFilterButtonDisabled(true);
    setWebsiteDisabled(true);
    setShouldRefreshCards(prev => prev + 1);
  };

  const handleChangeDropdown = (e, filterName) => {
    const selectedValue = e.target.value;
    if (selectedValue === "all") {
      setFilterButtonDisabled(true);
    } else {
      setFilterButtonDisabled(false);
    }
    if (filterName === "statusFilter") {
      setStatusFilter(selectedValue);
    } else if (filterName === "amountFilter") {
      setAmountFilter(selectedValue);
    } else if (filterName === "tenantFilter") {
      if (selectedValue !== "all") {
        setWebsiteDisabled(false);
      } else {
        setWebsiteDisabled(true);
      }
      setTenantFilter(selectedValue);
    }
  };

  const handleFilterData = () => {
    setCurrentPage(1);
    setEnableFilterButton(true);
    setLoading(true);
    setShouldFetchData(1);
    setTransactionData([]);
    setFilterButtonDisabled(true);
    setResetButtonDisabled(false);
    setShouldRefreshCards(prev => prev + 1);

  };

  const SelectDropdown_SX = {
    "& .MuiSelect-select": {
      padding: "11px",
      textAlign: "start",
    },
    fieldset: {
      borderColor: "#e1e3ea !important",
      borderRadius: "30px",
    },
  };

  const columns = [
    {
      field: "companyName",
      headerName: "Company Name",
      minWidth: 160,
      flex: 1,
      renderCell: (params) => <>{params.row.companyName || "-"}</>,
    },
    {
      field: "orderId",
      headerName: "Order ID",
      minWidth: 180,
      flex: 1.2,
      renderCell: (params) => <>{params.row?.orderId ? `#${params.row.orderId}` : "-"}</>,
    },
    {
      field: "transactionId",
      headerName: "Transaction ID",
      minWidth: 180,
      flex: 1.2,
    },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 120,
      flex: 0.8,
      renderCell: (params) => <>{formatCurrency(params.row?.amount) || "-"}</>,
    },
    {
      field: "billingCycle",
      headerName: "Billing Cycle",
      minWidth: 130,
      flex: 0.9,
      renderCell: (params) => {
        const cycle = params.row?.billingCycle;
        return <>{cycle === "monthly" ? "Monthly" : cycle === "yearly" ? "Yearly" : "-"}</>;
      },
    },
    {
      field: "transactionDate",
      headerName: "Transaction Date",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => <>{formatDateTime(params.row?.transactionDate, true)}</>,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.9,
      renderCell: (params) => {
        const statusMap = {
          0: { text: "Pending", status: "warning" },
          1: { text: "Success", status: "success" },
          2: { text: "Failed", status: "danger" },
          3: {text: "Refund", status: "default"}
        };

        const statusCode = String(params.row?.status ?? "0");
        const { text, status } = statusMap[statusCode] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({ status, title: text });
        // let color = "";
        // let label = "";

        // switch (params.row?.status) {
        //   case 0:
        //   case "0":
        //     color = "warning";
        //     label = "Pending";
        //     break;
        //   case 1:
        //   case "1":
        //     color = "success";
        //     label = "Success";
        //     break;
        //   case 2:
        //   case "2":
        //     color = "danger";
        //     label = "Failed";
        //     break;
        //   default:
        //     color = "secondary";
        //     label = "-";
        // }

        // return (
        //   <span
        //     className={`text-capitalize badge badge-light-${color}`}
        //     style={{ color }}
        //   >
        //     {label}
        //   </span>
        // );
      },
    },
    {
      field: "paymentMode",
      headerName: "Payment Mode",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const statusMap = {
          offline: { text: "Offline Payment", status: "scheduled" },
          online: { text: "Online Payment", status: "warning" },
        };

        const key = params.row?.paymentMode?.toLowerCase() ?? "";

        const { text, status } = statusMap[key] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({ status, title: text });
      },
    },
    {
      field: "invoice",
      headerName: "Invoice",
      minWidth: 100,
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          className="btn-custom-i text-primary"
          onClick={() => handleOpenInvoice(params.row)}
        >
          <MyDescriptionOutlinedIcon />
        </IconButton>
      ),
    },
  ];

  // const handlePdfDownload = async (pdf) => {
  //   const pdfUrl = `${MEDIA_URL}/${pdf}`;
  //   try {
  //     const response = await fetch(pdfUrl);
  //     const blob = await response.blob();
  //     const blobUrl = window.URL.createObjectURL(blob);

  //     const link = document.createElement("a");
  //     link.href = blobUrl;
  //     link.setAttribute("download", pdf);
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(blobUrl);
  //   } catch (error) {
  //     console.error("Error downloading PDF:", error);
  //   }
  // };

  const handleRowsPerPageChange = (event) => {
    const newValue = parseInt(event.target.value);
    setSelectedRowsPerPage(newValue);
    setCurrentPage(1);
    setLoading(true);
    setShouldFetchData(1);
    setTransactionData([]);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportOption = (type) => {
    setExportType(type);
    // Preselect visible columns except 'actions'
    let defaultColumns = columns.filter((col) => col.field !== "actions" && visibleColumns[col.field] !== false).map((col) => col.field);
    setSelectedColumns(defaultColumns);
    setExportDialogOpen(true);
    handleMenuClose();
  };

  const handleExportConfirm = async (exportColumnsArg) => {
    setExportLoader(true);
    let columnsData = exportColumnsArg || selectedColumns;

    const data = {
      exportType,
      exportScope,
      page: exportScope === "thisPage" ? currentPage : null,
      pageSize: exportScope === "thisPage" ? selectedRowsPerPage : null,
      status: statusFilter,
      amount: amountFilter,
      searchValue,
      startDate,
      endDate,
      exportColumns: columnsData,
    };
    await exportTransactionHistoryAction(data)
      .then((res) => {
        if (res.type === "application/json") {
          setSnackbar({
            open: true,
            message: "There is no data exist for export.",
            severity: "info",
          });
        } else {
          const blob = new Blob([res]);
          const timestamp = new Date().getTime();
          let fileName;

          // Set file name based on export type
          switch (exportType) {
            case "Excel":
              fileName = `transaction_${timestamp}.xlsx`;
              break;
            case "PDF":
              fileName = `transaction_report_${timestamp}.pdf`;
              break;
            default:
              fileName = `export_${timestamp}.txt`;
          }

          // Create a download link and trigger the download
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          URL.revokeObjectURL(link.href);
          document.body.removeChild(link);

          setSnackbar({
            open: true,
            message: "Data exported successfully",
            severity: "success",
          });
          return true;
        }
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response.data.message,
          severity: "error",
        });
      })
      .finally(() => {
        handleMenuClose();
        setExportLoader(false);
        setExportScope("thisPage");
        setExportDialogOpen(false);
      });
  };

  const handleOpenInvoice = (transaction) => {
    setSelectedTransaction(transaction);
    setInvoiceModalOpen(true);
  };

  const handleCloseInvoice = () => {
    setInvoiceModalOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <div>
      {!isDashboardView && <BreadcrumbsComponent />}

      {/* Summary Cards */}
      {!isDashboardView && (
        <div style={{ background: "#f1f5f9", padding: "1px" }}>
          <TransactionCards 
            handleSnackBar={handleSnackBar} 
            startDate={startDate}
            endDate={endDate}
            statusFilter={statusFilter}
            tenantFilter={tenantFilter}
            searchValue={searchValue}
            amountFilter={amountFilter}
            shouldRefresh={shouldRefreshCards}
          />
        </div>
      )}
      {/* End Summary Cards */}

      <DataGrid
        getRowId={(row) => row.transactionId} 
        localeText={{ toolbarColumns: "" }}
        rows={transactionData}
        columns={columns}
        autoHeight
        hideFooterRowCount={true}
        hideFooterPagination={true}
        hideFooter={true}
        loading={loading}
        disableRowSelectionOnClick
        columnVisibilityModel={visibleColumns}
        onColumnVisibilityModelChange={(newModel) => {
          setVisibleColumns(newModel);
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            SelectDropdown_SX,
            searchValue,
            handleSearchApi,
            handleFilterData,
            handleResetFilters,
            enableFilterButton,
            isDashboardView,
            handleChangeDropdown,
            statusFilter,
            amountFilter,
            tenantFilter,
            merchantData,
            handleExportOption,
            handleExportClick,
            handleMenuClose,
            isMenuOpen,
            anchorEl,
            filterButtonDisabled,
            resetButtonDisabled,
            hasPermission,
            exportLoader,
            transactionData,
            websiteDisabled,
            setWebsiteDisabled,
            selectedRange,
            onRangeChange: handleRangeChange,
          },
        }}
        components={{
          loadingOverlay: () => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100px",
              }}
            >
              <CircularProgress
                color="primary"
                size={24}
                style={{ marginRight: 10 }}
              />
              Loading...
            </div>
          ),
          noRowsOverlay: () =>
            !loading && transactionData.length === 0 ? (
              <div
                style={{
                  fontWeight: "bold",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                No Records Found
              </div>
            ) : null,
        }}
        sx={DataGrid_SX}
      />

      {!isDashboardView && transactionData.length > 0 && (
        <div className="card-footer d-flex align-items-center justify-content-between mt-7">
          <div className="d-flex align-items-center">
            <label className="fw-semibold me-3">Row per page:</label>
            <FormControl
              variant="standard"
              style={{
                minWidth: 50,
              }}
            >
              <Select
                labelId="rows-per-page-label"
                id="rows-per-page"
                value={selectedRowsPerPage}
                onChange={handleRowsPerPageChange}
                label="Rows per page"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <Pagination
            count={Math.ceil(totalRecords / selectedRowsPerPage)}
            siblingCount={0}
            page={currentPage}
            onChange={(event, value) => {
              if (value !== currentPage) {
                setCurrentPage(value);
                setShouldFetchData(1);
                setTransactionData([]);
                setLoading(true);
              }
            }}
            variant="outlined"
            shape="rounded"
            className="custom-pagination mt-0 ms-5"
          />
        </div>
      )}

      {exportDialogOpen && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          exportScope={exportScope}
          setExportScope={setExportScope}
          onExport={handleExportConfirm}
          columns={columns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
        />
      )}

      {invoiceModalOpen && (
        <TransactionInvoiceModal
          open={invoiceModalOpen}
          handleClose={handleCloseInvoice}
          transactionData={selectedTransaction}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default TransactionSection;
